import {Component, OnDestroy, OnInit, Signal, signal, WritableSignal} from '@angular/core';
import {ButtonDirective} from "primeng/button";
import {DatePipe, DecimalPipe, NgClass, NgIf} from "@angular/common";
import {SkeletonModule} from "primeng/skeleton";
import {PseTransactionService} from "../../../../services/recaudo/pse-transaction.service";
import {RequestFinalizaPago, ResultFinalizaTransaccion} from "../../../../shared/interfaces/recaudo";
import {SessionStorageService, AlertIcon, AlertService, LoadingPanelService, Utils, SafeResourcePipe} from "lib-base";
import {PseTransactionSolicitud} from "../../../../shared/interfaces/pse";
import {Router} from "@angular/router";
import {PseTransaccionEstado} from "../../../../shared/enums/PseTransaccionEstado";
import {FacturaService} from "../../../../services/liquidacion/factura.service";
import {firstValueFrom, forkJoin, Subject, takeUntil, timer} from "rxjs";
import {TranslateService} from "@ngx-translate/core";


@Component({
  selector: 'app-comprobante-pago-pse',
  standalone: true,
  imports: [
    ButtonDirective,
    DatePipe,
    DecimalPipe,
    NgIf,
    SafeResourcePipe,
    SkeletonModule,
    NgClass
  ],
  templateUrl: './comprobante-pago-pse.component.html',
  styleUrl: './comprobante-pago-pse.component.scss'
})
export class ComprobantePagoPseComponent implements OnInit, OnDestroy {
  recibo?: Blob;
  url?: string;
  resultTransaction?: ResultFinalizaTransaccion;
  solicitudPse?: PseTransactionSolicitud;
  translations: any = {};
  numeroRadicado: WritableSignal<string> = signal<string>('');
  title: Signal<string> = signal('comprobante-pago');
  private TIME: number = 3 * 60 * 1_000;
  private destroy$ = new Subject<void>();

  constructor(private pseTransactionService: PseTransactionService,
              private sessionService: SessionStorageService, private _t: TranslateService,
              private _lps: LoadingPanelService, private facturaService: FacturaService,
              private alertService: AlertService, private router: Router) {
  }

  ngOnInit(): void {
    this.getTranslations()
      .then(() => this.getDetalleSolicitud());
  }

  ngOnDestroy(): void {
    this.completeSchedule();
    this.sessionService.removeItem(this.sessionService.SOLICITUD_PSE);
  }

  private async getTranslations() {
    this.translations = await firstValueFrom(forkJoin({
      subHeader: this._t.get('components.app-comprobante-pago.sub-header'),
      header: this._t.get('components.app-comprobante-pago.header'),
      subHeader2: this._t.get('components.app-comprobante-pago.sub-header2'),
      estado: this._t.get('components.app-comprobante-pago.state'),
      headerTransaction: this._t.get('components.app-comprobante-pago.header-transaccion'),
      nombreBanco: this._t.get('components.app-comprobante-pago.bank'),
      valorPago: this._t.get('components.app-comprobante-pago.value'),
      tramite: this._t.get('components.app-comprobante-pago.description-pay'),
      fechaTransaccion: this._t.get('components.app-comprobante-pago.trans-date'),
      volver: this._t.get('form.button.finalizar'),
      horaTransaccion: this._t.get('components.app-comprobante-pago.trans-hour'),
      cus: this._t.get('components.app-comprobante-pago.cus'),
      codServicio: this._t.get('components.app-comprobante-pago.srv-code'),
      ticket: this._t.get('components.app-comprobante-pago.ticket'),

      messageWarnMsg: this._t.get('components.app-comprobante-pago.msg-warn-detalle-factura'),
      titleWarnMsg: this._t.get('form.msg.warn-summary'),
      continuar: this._t.get('common.continuar'),
      descargar: this._t.get('common.descargar'),
      imprimirComprobante: this._t.get('components.app-comprobante-pago.radicar'),
      detalleTransaccionTitulo: this._t.get('components.app-comprobante-pago-pse.detalle-transaccion'),
      messageWarnFactura: this._t.get('components.app-comprobante-pago-pse.warn-imprimir-factura'),
      detalleTransaccionDescripcion: this._t.get('components.app-comprobante-pago-pse.descripcion-detalle-transaccion'),
    }));
  }

  private getDetalleSolicitud() {
    const detalleTransaccion = this.sessionService.getItem(this.sessionService.SOLICITUD_PSE) as PseTransactionSolicitud;
    if (!detalleTransaccion) {
      this.alertService.show({
        data: {
          icon: AlertIcon.WARN,
          title: this.translations.detalleTransaccionTitulo,
          text: this.translations.detalleTransaccionDescripcion,
          showAccept: true,
        }, autoClose: true,
        timer: 3_000
      }).subscribe((value) => {
        if (value.accept) {
          this.router.navigate(['/tramites/estado-tramite']).then();
        }
      });
      return;
    }
    this.solicitudPse = detalleTransaccion;
    this.numeroRadicado.set(this.solicitudPse.radicado ?? '');
    this.finalizePayment()
  }

  private finalizePayment() {
    if (!this.solicitudPse)
      return;

    const body: RequestFinalizaPago = {
      solicitud: this.solicitudPse?.solicitud,
      ticket: this.solicitudPse.ticketId,
      bankName: this.solicitudPse?.nombreBanco,
      tipoDocumento: this.solicitudPse?.tipoFactura
    };
    this._lps.show();

    this.pseTransactionService.finalizaTransactionByReferencia(body).subscribe({
      next: result => {
        this.resultTransaction = result;
        this._lps.hide();
        console.log(this.resultTransaction);
        if (this.resultTransaction.estado == PseTransaccionEstado.STATE_OK) {
          this.getFacturaPagada();
          this.completeSchedule();

        } else if (result.estado == PseTransaccionEstado.STATE_PENDING) {
          this.scheduleNextCheck();
        }
      }
    })
  }
// TODO Configurar 3 intentos como maximo
  private scheduleNextCheck(): void {
    timer(this.TIME)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.finalizePayment());
  }

  private completeSchedule(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private getFacturaPagada() {
    this._lps.show();
    if (!this.solicitudPse) return;
    const lote = this.solicitudPse?.lote;
    this.facturaService.imprimirFactura(lote).subscribe({
      next: result => {
        this._lps.hide();
        if (result.size > 0) {
          this.recibo = result;
          this.url = URL.createObjectURL(result);
        }
      }, error: () => {
        this._lps.hide();
        this.alertService.show({
          icon: AlertIcon.WARN,
          title: this.translations.titleWarnMsg,
          text: this.translations.messageWarnFactura,
          showAccept: true
        })
      }
    })
  }

  redirectToHome = (): void => {
    this.router.navigate(['/tramites', 'estado-tramite'])
      .then();
  }

  get isMobile() {
    return Utils.isMobile();
  }


}


